import type { ProductSizeOption } from '@repo/api-contracts';
import { useState } from 'react';
import {
  allocationForSingleDestination,
  applySizeAllocation,
  remainingBySize,
  type SizeFieldValues,
  type SizeQuantityRow,
  sizeTotalsFromFields,
  sumDestinationQuantities,
} from '@/entities/quote';
import { startQuantity } from './start-quantity';
import { type DestinationDraft, useDestinationList } from './use-destination-list';

interface SizeDestinationValues {
  readonly quantity: string;
  readonly sizes: readonly SizeQuantityRow[];
}

interface QuoteRequestDestinationsOptions<T extends SizeDestinationValues> {
  readonly sizeOptions: readonly ProductSizeOption[];
  readonly minOrderQty: number;
  readonly initialQuantity: number | null | undefined;
  readonly blankDestination: () => T;
}

/** 네 요청 폼이 함께 쓰는 배송지 상태: 단일/복수 전환과 제출 직전 사이즈 배분. */
export const useQuoteRequestDestinations = <T extends SizeDestinationValues>({
  sizeOptions,
  minOrderQty,
  initialQuantity,
  blankDestination,
}: QuoteRequestDestinationsOptions<T>) => {
  const hasSizes = sizeOptions.length > 0;
  const destinations = useDestinationList<T>(
    {
      ...blankDestination(),
      quantity: hasSizes ? '' : startQuantity(minOrderQty, initialQuantity),
    },
    blankDestination,
  );
  const [sizeFields, setSizeFields] = useState<SizeFieldValues>({});
  const sizeTotals = sizeTotalsFromFields(sizeOptions, sizeFields);
  const totalQuantity = hasSizes
    ? (sizeTotals?.total ?? null)
    : sumDestinationQuantities(destinations.items.map((item) => item.value.quantity));
  const showSizeRows = hasSizes && destinations.items.length >= 2;

  const add = (): void => {
    const first = destinations.items[0];
    if (hasSizes && destinations.items.length === 1 && first) {
      destinations.update(first.key, {
        ...first.value,
        sizes: allocationForSingleDestination(sizeTotals?.bySize ?? []),
      });
    }
    destinations.add();
  };
  const remove = (key: string): void => {
    if (hasSizes && destinations.items.length === 2) {
      const remaining = destinations.items.find((item) => item.key !== key);
      if (remaining) destinations.update(remaining.key, { ...remaining.value, sizes: [] });
    }
    destinations.remove(key);
  };
  const changeSizeField = (id: string, value: string): void => {
    setSizeFields((previous) => ({ ...previous, [id]: value }));
  };
  const toSubmissionDestinations = (): readonly DestinationDraft<T>[] => {
    const allocations = applySizeAllocation(
      sizeOptions,
      sizeTotals?.bySize ?? [],
      destinations.items,
    );
    return destinations.items.map((item) => {
      const allocation = allocations.find((row) => row.key === item.key);
      return allocation
        ? {
            ...item,
            value: { ...item.value, sizes: allocation.sizes, quantity: allocation.quantity },
          }
        : item;
    });
  };

  return {
    ...destinations,
    add,
    remove,
    hasSizes,
    sizeFields,
    changeSizeField,
    totalQuantity,
    showSizeRows,
    remainingSizes: remainingBySize(
      sizeOptions,
      sizeTotals?.bySize ?? [],
      destinations.items.map((item) => item.value),
    ),
    toSubmissionDestinations,
  };
};
